
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useLoaderContext } from '../components/Loader/LoaderContext';
import { getRolePermissions } from '../features/auth/services/rolePermissions.service';

interface Props {
  children: ReactNode;
  requiredPermission: string;
  deniedTo?: string;
}

interface RolePermission {
  permissionName: string;
  permissionActive: boolean;
  permissionDelete: boolean;
  roleActive: boolean;
  roleDelete: boolean;
  rolePermissionActive: boolean;
  rolePermissionDelete: boolean;
}

interface RolePermissionsResponse {
  permissions?: RolePermission[];
}

type PermissionStatus = 'loading' | 'allowed' | 'denied';

/**
 * Supports permission names such as:
 * PublicReport
 * WFM.PublicReport
 * SomeDynamicRole.PublicReport
 *
 * The role name is never hardcoded.
 */
const normalizePermissionName = (name: string): string => {
  const trimmedName = name.trim();
  const lastSeparatorIndex = trimmedName.lastIndexOf('.');

  return (
    lastSeparatorIndex >= 0
      ? trimmedName.substring(lastSeparatorIndex + 1)
      : trimmedName
  ).trim().toLowerCase();
};

const ReportPermissionRoute = ({
  children,
  requiredPermission,
  deniedTo = '/404',
}: Props) => {
  const [status, setStatus] = useState<PermissionStatus>('loading');
  const { hideLoader } = useLoaderContext();

  useEffect(() => {
    let cancelled = false;

    const verifyPermission = async () => {
      setStatus('loading');

      try {
        const token = localStorage.getItem('token');
        const employeeCode = localStorage.getItem('employeeCode');
        const roleIdValue = localStorage.getItem('roleId');

        if (!token || !employeeCode || !roleIdValue) {
          if (!cancelled) {
            setStatus('denied');
          }
          return;
        }

        const roleId = Number(roleIdValue);

        if (!Number.isInteger(roleId) || roleId <= 0) {
          if (!cancelled) {
            setStatus('denied');
          }
          return;
        }

        const result = (await getRolePermissions(
          employeeCode,
          roleId,
        )) as RolePermissionsResponse;

        const permissions = Array.isArray(result?.permissions)
          ? result.permissions
          : [];

        const requiredName = normalizePermissionName(requiredPermission);

        const permitted = permissions.some((permission) => {
          const permissionName = normalizePermissionName(
            permission.permissionName ?? '',
          );

          return (
            permissionName === requiredName &&
            permission.permissionActive === true &&
            permission.permissionDelete === false &&
            permission.roleActive === true &&
            permission.roleDelete === false &&
            permission.rolePermissionActive === true &&
            permission.rolePermissionDelete === false
          );
        });

        if (!cancelled) {
          setStatus(permitted ? 'allowed' : 'denied');
        }
      } catch (error) {
        console.error('Permission verification failed:', error);

        if (!cancelled) {
          setStatus('denied');
        }
      }
    };

    void verifyPermission();

    return () => {
      cancelled = true;
    };
  }, [requiredPermission]);

  useEffect(() => {
    if (status !== 'loading') {
      hideLoader();
    }
  }, [status, hideLoader]);

  if (status === 'loading') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#eef3f8]">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />

          <p className="mt-4 text-sm text-slate-600">
            Verifying access...
          </p>
        </div>
      </main>
    );
  }

  if (status === 'denied') {
    return <Navigate to={deniedTo} replace />;
  }

  return <>{children}</>;
};

export default ReportPermissionRoute;
